import * as THREE from 'three';
import { TerrainFeature } from './TerrainFeature';
import { MountainPeakConfig, TerrainBounds, TERRAIN_COLORS } from './TerrainConfig';

/**
 * Mountain Peak - Large conical peaks at map corners.
 * Height range: 30-50m for Tier 3-4 highway access.
 * Features irregular noise variation for natural appearance.
 */
export class MountainPeak extends TerrainFeature {
  private peakHeight: number;
  private baseRadius: number;
  private irregularity: number;
  private noiseOffset: number; // Unique offset for each mountain

  constructor(scene: THREE.Scene, config: MountainPeakConfig) {
    super(scene, config.position, config.rotation || 0, 'mountain_peak');

    this.peakHeight = config.peakHeight;
    this.baseRadius = config.baseRadius;
    this.irregularity = config.irregularity || 0.15;
    this.noiseOffset = Math.random() * 1000; // Unique per instance

    this.createMesh();
    this.addToScene();
  }

  private createMesh(): void {
    // Create irregular cone geometry with noise
    const radialSegments = 24;
    const heightSegments = 12;
    const geometry = new THREE.BufferGeometry();
    const vertices: number[] = [];
    const indices: number[] = [];

    // Generate vertices in rings from base to peak
    for (let h = 0; h <= heightSegments; h++) {
      const heightProgress = h / heightSegments;
      const ringY = heightProgress * this.peakHeight;
      const ringRadius = this.baseRadius * (1 - heightProgress * 0.95); // Taper to 5% at peak

      for (let r = 0; r <= radialSegments; r++) {
        const theta = (r / radialSegments) * Math.PI * 2;

        // Add irregular noise to radius
        const noise = this.getNoiseValue(theta, heightProgress);
        const noiseRadius = ringRadius * (1 + noise * this.irregularity);

        const x = Math.cos(theta) * noiseRadius;
        const z = Math.sin(theta) * noiseRadius;

        vertices.push(x, ringY, z);
      }
    }

    // Generate indices for triangles
    for (let h = 0; h < heightSegments; h++) {
      for (let r = 0; r < radialSegments; r++) {
        const a = h * (radialSegments + 1) + r;
        const b = a + 1;
        const c = a + (radialSegments + 1);
        const d = c + 1;

        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    geometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    // Main mountain mesh
    const material = this.createTerrainMaterial(TERRAIN_COLORS.BASE);
    const mountainMesh = new THREE.Mesh(geometry, material);
    this.mesh.add(mountainMesh);

    // Add peak marker
    this.createPeakMarker();

    // Add support structures at base
    this.createBaseStructures();

    // Add contour lines
    this.createContourLines();
  }

  private getNoiseValue(theta: number, height: number): number {
    // Simple pseudo-noise based on trigonometric functions
    const x = theta + this.noiseOffset;
    const y = height * 10 + this.noiseOffset;

    return (
      Math.sin(x * 3) * 0.3 +
      Math.sin(x * 7 + y) * 0.2 +
      Math.cos(y * 2 + x * 2) * 0.3 +
      Math.sin(x * 11 - y * 3) * 0.2
    );
  }

  private createPeakMarker(): void {
    // Glowing summit beacon
    const beaconGeometry = new THREE.ConeGeometry(1, 4, 8);
    const beaconMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.PEAK_GLOW,
      emissive: TERRAIN_COLORS.PEAK_GLOW,
      emissiveIntensity: 3,
      transparent: true,
      opacity: 0.9,
    });

    const beacon = new THREE.Mesh(beaconGeometry, beaconMaterial);
    beacon.position.y = this.peakHeight + 2;
    this.mesh.add(beacon);

    // Rotating ring around peak
    const ringGeometry = new THREE.TorusGeometry(3, 0.2, 8, 32);
    const ringMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissive: TERRAIN_COLORS.EDGE_HIGHLIGHT,
      emissiveIntensity: 2,
      transparent: true,
      opacity: 0.7,
    });

    const ring = new THREE.Mesh(ringGeometry, ringMaterial);
    ring.position.y = this.peakHeight;
    ring.rotation.x = Math.PI / 2;
    this.mesh.add(ring);
  }

  private createBaseStructures(): void {
    // Add pillar supports at base (matching highway aesthetic)
    const pillarCount = 6;
    const pillarRadius = this.baseRadius * 1.1;
    const pillarHeight = 8;

    const pillarGeometry = new THREE.CylinderGeometry(1.5, 2, pillarHeight, 6);
    const pillarMaterial = new THREE.MeshStandardMaterial({
      color: TERRAIN_COLORS.BASE_LIGHT,
      metalness: 0.8,
      roughness: 0.4,
    });

    for (let i = 0; i < pillarCount; i++) {
      const theta = (i / pillarCount) * Math.PI * 2;
      const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
      pillar.position.set(
        Math.cos(theta) * pillarRadius,
        pillarHeight / 2,
        Math.sin(theta) * pillarRadius
      );
      this.mesh.add(pillar);

      // Add glowing band on pillar
      const bandGeometry = new THREE.TorusGeometry(1.8, 0.2, 8, 16);
      const bandMaterial = new THREE.MeshStandardMaterial({
        color: TERRAIN_COLORS.GRID_LINE,
        emissive: TERRAIN_COLORS.GRID_LINE,
        emissiveIntensity: 1,
        transparent: true,
        opacity: 0.6,
      });

      const band = new THREE.Mesh(bandGeometry, bandMaterial);
      band.position.set(
        Math.cos(theta) * pillarRadius,
        pillarHeight - 1,
        Math.sin(theta) * pillarRadius
      );
      band.rotation.x = Math.PI / 2;
      this.mesh.add(band);
    }
  }

  private createContourLines(): void {
    // Add horizontal contour lines at height intervals
    const contourInterval = 10;
    const numContours = Math.floor(this.peakHeight / contourInterval);

    const lineMaterial = new THREE.LineBasicMaterial({
      color: TERRAIN_COLORS.GRID_LINE,
      transparent: true,
      opacity: 0.4,
    });

    for (let c = 1; c <= numContours; c++) {
      const contourHeight = c * contourInterval;
      const heightProgress = contourHeight / this.peakHeight;
      const contourRadius = this.baseRadius * (1 - heightProgress * 0.95);

      const points: THREE.Vector3[] = [];
      const segments = 48;

      for (let i = 0; i <= segments; i++) {
        const theta = (i / segments) * Math.PI * 2;
        const noise = this.getNoiseValue(theta, heightProgress);
        const r = contourRadius * (1 + noise * this.irregularity);

        points.push(
          new THREE.Vector3(
            Math.cos(theta) * r,
            contourHeight + 0.1,
            Math.sin(theta) * r
          )
        );
      }

      const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(lineGeometry, lineMaterial);
      this.mesh.add(line);
    }
  }

  /**
   * Get height at world position using conical profile with noise.
   */
  getHeightAtPosition(worldPos: THREE.Vector3): number | null {
    const local = this.worldToLocal(worldPos);

    // Calculate distance from center
    const distance = Math.sqrt(local.x * local.x + local.z * local.z);

    // Check if within base radius (with margin)
    if (distance > this.baseRadius * 1.2) {
      return null;
    }

    // Calculate angle for noise
    const theta = Math.atan2(local.z, local.x);

    // Binary search to find height at this distance
    // The noise varies with height, so we need to iterate
    let height = 0;
    const maxIterations = 10;

    for (let i = 0; i < maxIterations; i++) {
      const heightProgress = height / this.peakHeight;
      const noise = this.getNoiseValue(theta, heightProgress);
      const expectedRadius = this.baseRadius * (1 - heightProgress * 0.95);
      const noiseRadius = expectedRadius * (1 + noise * this.irregularity);

      if (distance > noiseRadius) {
        // At base or outside
        break;
      }

      // Calculate new height estimate based on linear interpolation
      const radiusRatio = distance / noiseRadius;
      height = this.peakHeight * (1 - radiusRatio) * (1 - heightProgress * 0.05);

      // Converge
      if (Math.abs(height - heightProgress * this.peakHeight) < 0.1) {
        break;
      }
    }

    // Smooth falloff near edges
    const edgeFalloff = 1 - Math.pow(distance / this.baseRadius, 2);
    height *= Math.max(0, edgeFalloff);

    // Only return significant heights
    if (height < 0.5) {
      return null;
    }

    return height;
  }

  getBounds(): TerrainBounds {
    const extent = this.baseRadius * 1.2;
    return {
      minX: this.position.x - extent,
      maxX: this.position.x + extent,
      minZ: this.position.z - extent,
      maxZ: this.position.z + extent,
    };
  }
}
